import { yupResolver } from '@hookform/resolvers/yup';
import { Space, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import EditHistory from '@/pages/master-data/components/edit-history/EditHistory';

import { BaseButton } from '@/components/base-button/BaseButton';
import { TYPE_ICON, showBasePopup } from '@/components/base-popup/BasePopup';
import { IconExport } from '@/components/icon-svg/IconSvg';

import { IAlertNotificationState, setAlertNotification } from '@/redux/globalReducer';

import useEventListener from '@/hooks/useEventListener';
import useFetch from '@/hooks/useFetch';
import useMutation from '@/hooks/useMutation';

import { WINDOW_EVENTS } from '@/utils/constants';
import { API } from '@/utils/constants/Apis';
import { TYPE } from '@/utils/constants/AppConstants';
import { HTTP_STATUS_CODE } from '@/utils/constants/Http';
import { DataViewer, handleCommonError } from '@/utils/helpers/common';
import { downloadSingleFileFromURI } from '@/utils/helpers/fileHelper';
import { renderProjectCode } from '@/utils/helpers/project';
import { ICategory } from '@/utils/interfaces/template';
import { addProjectMemo, getDetailProjectMemo, getProjectInfo, updateProjectMemo } from '@/utils/services/ProjectApiService';

import { IProjectInfo } from '../add-edit/models';
import { ProjectMemoForm } from './components/ProjectMemoForm';
import { DEFAULT_DETAIL_PROJECT_MEMO } from './constants';
import { ICategories, IOptions, IProjectMemoRes } from './model';
import { FormProjectMemo } from './validation/FormValidation';

declare global {
  interface Window {
    clearForm: () => void;
  }
}

const ProjectMemo = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [projectInfo, setProjectInfo] = useState<IProjectInfo>();
  const { data: category } = useFetch<ICategory[] | null>(
    projectInfo?.projectAssignmentInfo?.countryId ? API.GET_CATEGORY(projectInfo?.projectAssignmentInfo?.countryId ?? '', 'PROJECT', id ?? '') : '',
    'GET'
  );
  const [projectMemo, setProjectMemo] = useState<IProjectMemoRes>(DEFAULT_DETAIL_PROJECT_MEMO);
  const [categoryOptions, setCategoryOptions] = useState<IOptions[]>([]);
  const [selectedCategoryLabelMap, setSelectedCategoryLabelMap] = useState<Map<string, string>>(new Map());
  const [isSaved, setIsSaved] = useState<boolean>(false);

  const { mutate: exportPdf } = useMutation(
    `/prj/projectMemos/${id}/export`,
    {
      method: 'POST',
      bodyType: 'json',
      showToastError: true
    },
    {
      responseType: 'blob'
    }
  );

  useEffect(() => {
    hanldeGetProjectInfo();
    handleGetDetailProjectMemo();
  }, [id]);

  useEffect(() => {
    if (!category) {
      setCategoryOptions([]);
      return;
    }

    const newCategoryOptions = category.map((item: ICategory) => ({
      value: `${item.id}::${item.name}`,
      label: item.name
    }));

    setCategoryOptions(newCategoryOptions);
  }, [category]);

  useEventListener(WINDOW_EVENTS.BEFORE_UNLOAD, (e) => {
    if (isDirty && !isSubmitting) {
      e.preventDefault();
      e.focus();
    }
  });

  const methods = useForm<IProjectMemoRes>({
    mode: 'onChange',
    defaultValues: DEFAULT_DETAIL_PROJECT_MEMO,
    resolver: yupResolver(FormProjectMemo)
  });

  const { isDirty, isSubmitting } = methods.formState;

  useEffect(() => {
    window.clearForm = () => {
      methods.reset(DEFAULT_DETAIL_PROJECT_MEMO);
    };
  }, []);

  const getVisaCategories = (selectedValues: string[]): ICategories[] => {
    return (selectedValues ?? []).map((selectedValue, index) => {
      const [id, currentName] = selectedValue.split('::');
      const matchedOption = categoryOptions.find((option) => option.value === selectedValue);

      return {
        id: id ?? selectedValue,
        name: matchedOption?.label ?? currentName ?? selectedCategoryLabelMap.get(String(id)) ?? '',
        order: index + 1
      };
    });
  };

  const handleCategoryAdded = (newCategory: IOptions) => {
    const newOptionValue = `${newCategory.value}::${newCategory.label}`;
    setCategoryOptions((prev) => {
      if (prev.some((item) => item.value === newOptionValue)) {
        return prev;
      }

      const option: IOptions = {
        value: newOptionValue,
        label: String(newCategory.label),
        id: newCategory.value
      };

      return [...prev, option];
    });

    setSelectedCategoryLabelMap((prev) => {
      const m = new Map(prev);
      m.set(String(newCategory.value), String(newCategory.label));
      return m;
    });
  };

  const hanldeGetProjectInfo = async () => {
    if (!id) return;
    try {
      const { data } = await getProjectInfo(String(id));
      setProjectInfo(data);
    } catch (e) {}
  };

  const handleGetDetailProjectMemo = async () => {
    if (!id) return;
    try {
      const { data } = await getDetailProjectMemo(String(id));
      if (data?.id) {
        const selectedLabelMap = new Map<string, string>(
          (data.visaCategories ?? []).map((item: ICategories) => [String(item.id), String(item.name ?? '')])
        );
        const projectMemoData = {
          ...data,
          visaCategories: (data.visaCategories ?? []).map((item: ICategories) => `${item.id}::${item.name}`)
        };
        setProjectMemo(data);
        setSelectedCategoryLabelMap(selectedLabelMap);
        methods.reset(projectMemoData);
      }
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setProjectMemo(DEFAULT_DETAIL_PROJECT_MEMO);
      }
    }
  };

  const handleAddProjectMemo = async (value: IProjectMemoRes) => {
    if (!id) return;
    try {
      const { id: memoId, projectId, ...rest } = value;
      const dataReq = {
        ...rest,
        visaCategories: getVisaCategories(value.visaCategories as unknown as string[])
      };
      const { data } = await addProjectMemo(id, dataReq);
      if (data) {
        handleGetDetailProjectMemo();
        dispatch(
          setAlertNotification({
            show: true,
            type: TYPE.SUCCESS,
            message: t('common:MSG_C_003', {
              item: t('button:keep')
            })
          })
        );
      }
    } catch (e: any) {
      const callback = (mess: IAlertNotificationState) => dispatch(setAlertNotification(mess));
      handleCommonError(e, '', callback);
    }
  };

  const handleUpdateProjectMemo = async (value: IProjectMemoRes) => {
    if (!id) return;
    try {
      const { id: memoId, projectId, ...rest } = value;
      const dataReq = {
        ...rest,
        visaCategories: getVisaCategories(value.visaCategories as unknown as string[])
      };
      const { data } = await updateProjectMemo(id, dataReq);
      if (data) {
        handleGetDetailProjectMemo();
        dispatch(
          setAlertNotification({
            show: true,
            type: TYPE.SUCCESS,
            message: t('common:MSG_C_003', {
              item: t('button:keep')
            })
          })
        );
      }
    } catch (e: any) {
      const callback = (mess: IAlertNotificationState) => dispatch(setAlertNotification(mess));
      handleCommonError(e, '', callback);
    }
  };

  const onCancel = async () => {
    const showPopup = await showBasePopup({
      title: String(t('dialog:cancel_editing:header')),
      msg: String(t('dialog:cancel_editing:content')),
      type: TYPE_ICON.CONFIRM_CANCEL_EDIT
    });
    if (showPopup !== TYPE_ICON.CONFIRM) return;

    const resetProjectMemo = {
      ...projectMemo,
      visaCategories: (projectMemo.visaCategories ?? []).map((item: ICategories) => `${item.id}::${item.name}`)
    } as unknown as IProjectMemoRes;

    methods.reset(resetProjectMemo);
  };

  const onSubmit = async (value: IProjectMemoRes) => {
    try {
      if (!projectMemo?.id) await handleAddProjectMemo(value);
      else await handleUpdateProjectMemo(value);
      setIsSaved(true);
    } catch (e) {}
  };

  const exportPDF = async () => {
    if (!id) return;
    const res = await exportPdf({});
    if (res?.status === HTTP_STATUS_CODE.SUCCESS) {
      const applicantNames = `${projectInfo?.projectApplicant.lastName} ${projectInfo?.projectApplicant.firstName}`;
      const fileName = `${projectInfo?.code}_${applicantNames}_${t('project_memo:title')}.pdf`;
      downloadSingleFileFromURI(res?.data, fileName);
    }
  };

  useEffect(() => {
    if (!isDirty) {
      setIsSaved(true);
    } else {
      setIsSaved(false);
    }
  }, [isDirty]);

  useEffect(() => {
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);

  if (!id) return <></>;

  const title = renderProjectCode(projectInfo, t('project_memo:project_id', { id: projectInfo?.code }));

  // Reload window if user login
  const handleStorageEvent = (event: StorageEvent) => {
    if (event.key !== 'userSignedIn') {
      return;
    }
    window.location.reload();
  };

  return (
    <div id='project-of-memo' className='p-[20px] bg-[#f5f5f5] h-screen'>
      <div className='h-[calc(100vh-48px)] overflow-y-auto'>
        <FormProvider {...methods}>
          <div className='header flex justify-between mb-[12px]'>
            <div>
              <p className='title-24 whitespace-pre-line'>{t('project_memo:title')}</p>
              <p className='body-400 text-textGray'>{title}</p>
            </div>
            <Space size={16} align='start'>
              <div className='min-w-[120px]'>
                <BaseButton block size='medium' type='tertiary' disabled={!isDirty || isSubmitting} onClick={onCancel}>
                  {t('button:cancel')}
                </BaseButton>
              </div>
              <div className='min-w-[120px]'>
                <BaseButton block size='medium' disabled={(!isDirty || isSubmitting) && !!projectMemo?.id} onClick={methods.handleSubmit(onSubmit)}>
                  <span> {t('button:keep')} </span>
                </BaseButton>
              </div>
              <div className='min-w-[120px]'>
                <BaseButton
                  onClick={exportPDF}
                  icon={<IconExport style={{ verticalAlign: 'sub', opacity: !isSaved ? 0.2 : 1 }} />}
                  type='tertiary'
                  size='medium'
                  disabled={!isSaved}
                  className='!py-2 !px-3'
                >
                  {t('project_detail:buttons:export_pdf')}
                </BaseButton>
              </div>
            </Space>
          </div>
          <div className='p-[24px] rounded-[10px] bg-white border border-gray2 flex flex-col gap-[12px]'>
            <div className='flex flex-row gap-[18px]'>
              <div className='flex-1'>
                <div className='flex items-center h-[32px] gap-[8px]'>
                  <p className='body-400 w-[180px] shrink-0'>{t('project_memo:corporate_name')}</p>
                  <p className='body-700 truncate'>{DataViewer.display(projectInfo?.projectApplicant?.currentCompany)}</p>
                </div>
              </div>
            </div>
            <div className='flex flex-row gap-[18px]'>
              <div className='flex-1'>
                <div className='flex items-center gap-[8px]'>
                  <p className='body-400 w-[180px] shrink-0 h-[32px] leading-[32px]'>{t('project_memo:applicant_name')}</p>
                  <p className='flex flex-wrap gap-[8px] overflow-hidden w-full'>
                    {
                      <Tag className='mr-0 h-[24px] flex items-center px-[8px] rounded-[4px] bg-gray2 max-w-[45%]'>
                        <span className='body-400 truncate'>{`${projectInfo?.projectApplicant?.lastName} ${projectInfo?.projectApplicant?.firstName}`}</span>
                      </Tag>
                    }
                    {projectInfo?.goWithFamily &&
                      projectInfo?.projectFamilies?.map((el, i) =>
                        (el?.lastName || el?.firstName) && !el?.nonVisaApplicationRequire ? (
                          <Tag
                            key={`applicantName-${el?.id}-${i}`}
                            className='mr-0 h-[24px] flex items-center px-[8px] rounded-[4px] bg-gray2 max-w-[45%]'
                          >
                            <span className='body-400 truncate'>{`${el?.lastName} ${el?.firstName}`}</span>
                          </Tag>
                        ) : (
                          <></>
                        )
                      )}
                  </p>
                </div>
              </div>
            </div>
            <div className='flex flex-row gap-[18px]'>
              <div className='flex-1'>
                <div className='flex items-center h-[32px] gap-[8px]'>
                  <p className='body-400 w-[180px] shrink-0'>{t('project_memo:country')}</p>
                  <p className='body-700'>{DataViewer.display(projectInfo?.projectAssignmentInfo?.countryName)}</p>
                </div>
              </div>
            </div>
            <div className='flex items-center h-[32px] gap-[8px]'>
              <p className='body-400 w-[180px] |shrink-0'>{t('basic_information:travel_type')}</p>
              <p className='body-700'>{DataViewer.display(projectInfo?.travelTypeName)}</p>
            </div>
            <ProjectMemoForm
              categoryOptions={categoryOptions}
              countryId={projectInfo?.projectAssignmentInfo?.countryId}
              changePicReasonInfo={projectInfo?.changePicReason}
              onCategoryAdded={handleCategoryAdded}
              selectedCategoryLabelMap={selectedCategoryLabelMap}
            />
          </div>
        </FormProvider>
        <EditHistory className='mt-2 mr-5' showCreateTime={false} data={projectMemo} />
      </div>
    </div>
  );
};

export default ProjectMemo;
